import React, {createContext, useContext, useEffect, useState} from "react";
import {useAnalytics} from "@unibuddy/tracking";

import {useUniversityContext} from "./UniversityProvider";
import {events} from "../analytics/productSpecification";

export enum PostMessageEventType {
    SMALL_DEVICE = "isSmallDevice",
    CHAT_VISIBLE = "chatVisible",
    SHOW_CTA_ACTIONS = "showCTAActions",
}

const initialContext = {
    [PostMessageEventType.SMALL_DEVICE]: false,
    [PostMessageEventType.CHAT_VISIBLE]: false,
    [PostMessageEventType.SHOW_CTA_ACTIONS]: false,
};

export type PostMessageContextType = typeof initialContext;

const PostMessageContext = createContext<PostMessageContextType>(initialContext);

export function usePostMessageContext() {
    return useContext(PostMessageContext);
}

type PostMessageProviderProps = {
    children: React.ReactNode;
};

function getEventTypeAndData(e: MessageEvent) {
    /* Our messages are currently all strings */
    if (typeof e.data === "string") {
        const data: string = e.data;
        const [postMessageEventType, eventValue] = data.split("=");
        return {
            eventType: postMessageEventType,
            value: eventValue === "true",
        };
    }
}
export function getSocketConnectionKey(universityId: string) {
    return `ASSISTANT_CONNECTION_${universityId}`;
}

export const PostMessageProvider = ({children}: PostMessageProviderProps) => {
    const urlParams = new URLSearchParams(window.location.search);
    const parentUrl = urlParams.get("parentUrl") ?? "*";
    const render = urlParams.get("render") ?? "*";
    const [context, setContext] = useState<PostMessageContextType>(initialContext);
    const {universityId} = useUniversityContext();
    const socketConnectionKey = getSocketConnectionKey(universityId);
    const canConnectToSocket = window.sessionStorage.getItem(socketConnectionKey) === "true";
    const {trackEvent} = useAnalytics();

    useEffect(() => {
        function messageListener(e: MessageEvent) {
            const message = getEventTypeAndData(e);
            if (message && message.eventType in initialContext) {
                setContext((previousContext) => ({
                    ...previousContext,
                    [message.eventType]: message.value,
                }));

                if (message.eventType === PostMessageEventType.CHAT_VISIBLE && render === "chat") {
                    if (message.value === true) {
                        trackEvent(events.productOpened.name, {
                            product: "Assistant",
                        });
                    } else if (message.value === false) {
                        trackEvent(events.productClosed.name, {
                            product: "Assistant",
                        });
                    }
                }

                if (
                    message.eventType === PostMessageEventType.CHAT_VISIBLE &&
                    !canConnectToSocket
                ) {
                    window.sessionStorage.setItem(socketConnectionKey, "true");
                }
            }
        }

        window.addEventListener("message", function (e): void {
            // Skip processing if we don't have a parent url
            // or if the event is not from the embedded version
            // and also if the event is not from the local version
            // (so that it runs locally)
            const isFromParent = parentUrl.startsWith(e.origin);
            const isFromLocal = parentUrl.startsWith("file://");
            if (!parentUrl || (!isFromParent && !isFromLocal)) {
                return;
            }

            messageListener(e);
        });
        return () => {
            window.removeEventListener("message", messageListener);
        };
    }, [universityId, canConnectToSocket, socketConnectionKey, parentUrl, render, trackEvent]);

    return <PostMessageContext.Provider value={context}>{children}</PostMessageContext.Provider>;
};
